/**
 * Ideas to try: votes, a pick, and for each pick who runs the test and what
 * result counts as success.
 */

import { Button, Caption1, Field, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import {
  Add16Regular,
  Checkmark16Regular,
  Lightbulb20Regular,
  Person16Regular,
  Subtract16Regular,
} from '@fluentui/react-icons'
import { pickOf } from '../../lib/planning'
import type { Plan, PlanEdit, PlanState } from '../../types/planning'
import { IssueNote } from './IssueNote'
import { CommitInput, IssueLink, PlanChip } from './PlanParts'

const useStyles = makeStyles({
  groups: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  groupHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
  },
  ideas: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
  },
  idea: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  picked: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    boxShadow: `inset 3px 0 0 ${tokens.colorBrandStroke1}`,
  },
  top: { display: 'flex', alignItems: 'center', gap: '6px' },
  title: { margin: 0, fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold },
  test: { margin: 0 },
  foot: { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: 'auto' },
  ask: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    flexGrow: 1,
    color: tokens.colorNeutralForeground3,
  },
  votes: { display: 'inline-flex', alignItems: 'center', gap: '2px' },
  count: { minWidth: '20px', textAlign: 'center', fontWeight: tokens.fontWeightSemibold },
  fields: { display: 'flex', flexDirection: 'column', gap: '6px' },
})

export function IdeaBoard({
  plan,
  state,
  edit,
  cycle = plan.cycle,
}: {
  plan: Plan
  state: PlanState
  edit: (e: PlanEdit) => void
  /** The stored cycle: an imported file may leave its own `cycle` out. */
  cycle?: string
}) {
  const styles = useStyles()
  const ideas = plan.ideas ?? []
  const groups = plan.idea_groups?.length ? plan.idea_groups : [{ t: 'Ideas' }]

  return (
    <div className={styles.groups}>
      {groups.map((g, gi) => {
        const list = ideas.filter((x) => (x.g ?? 0) === gi)
        if (!list.length) return null
        return (
          <div key={g.t}>
            <h3 className={styles.groupHead}>
              <Lightbulb20Regular aria-hidden="true" />
              {g.t}
            </h3>
            <div className={styles.ideas}>
              {list.map((x) => {
                const id = String(x.id)
                const p = pickOf(x.id, state)
                return (
                  <article
                    key={id}
                    className={mergeClasses(styles.idea, p.on && styles.picked)}
                    data-testid={`idea-${id}`}
                    tabIndex={-1}
                  >
                    <div className={styles.top}>
                      <IssueLink n={x.id} base={plan.repo_url} />
                      <PlanChip chip={x.cost} />
                    </div>
                    <h4 className={styles.title}>{x.t}</h4>
                    {x.test && (
                      <p className={styles.test}>
                        <b>Test:</b> {x.test}
                      </p>
                    )}
                    <IssueNote
                      cycle={cycle}
                      issue={x.id}
                      title={x.t}
                      sum={plan.sums?.[String(x.id)]?.s}
                    />
                    <div className={styles.foot}>
                      <Caption1 className={styles.ask}>
                        {x.ask && (
                          <>
                            <Person16Regular aria-hidden="true" />
                            Ask: {x.ask}
                          </>
                        )}
                      </Caption1>
                      <span className={styles.votes} role="group" aria-label={`Votes for ${x.t}`}>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Subtract16Regular />}
                          aria-label="Remove a vote"
                          onClick={() => edit({ op: 'incr', path: ['votes', id], value: -1 })}
                        />
                        {/* A span, not <output>: an output is a live region, and every
                            vote in the room was read out (#1578). */}
                        <span className={styles.count}>{state.votes?.[id] ?? 0}</span>
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<Add16Regular />}
                          aria-label="Add a vote"
                          onClick={() => edit({ op: 'incr', path: ['votes', id], value: 1 })}
                        />
                      </span>
                      <Button
                        size="small"
                        appearance={p.on ? 'primary' : 'outline'}
                        icon={<Checkmark16Regular />}
                        aria-pressed={p.on}
                        onClick={() => edit({ op: 'set', path: ['picks', id, 'on'], value: !p.on })}
                      >
                        {p.on ? 'Picked' : 'Pick'}
                      </Button>
                    </div>
                    {p.on && (
                      <div className={styles.fields}>
                        <Field label="Who runs the test" size="small">
                          <CommitInput
                            size="small"
                            placeholder="Name"
                            value={p.who}
                            onCommit={(v) => edit({ op: 'set', path: ['picks', id, 'who'], value: v })}
                          />
                        </Field>
                        <Field label="Success looks like" size="small">
                          <CommitInput
                            size="small"
                            placeholder="What result counts"
                            value={p.ok}
                            onCommit={(v) => edit({ op: 'set', path: ['picks', id, 'ok'], value: v })}
                          />
                        </Field>
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

